import {skipToken} from '@reduxjs/toolkit/query';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {reverse} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {CustomerMessageResponseBody, useListCustomerMessagesQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';

import {CommunicationChannelFilter} from '../types/CommunicationChannelFilter';
import {groupMessagesByDay} from '../utils/groupMessagesByDay';
import {sortMessagesChronologically} from '../utils/sortMessagesChronologically';

interface UseCommunicationThreadArgs {
  customerId: string;
  recordId?: string;
  resourceId?: EntityResourceIds;
}

export function useCommunicationThread(args: UseCommunicationThreadArgs) {
  const {data, isLoading, isError, isSuccess} = useListCustomerMessagesQuery(
    isNotNilOrEmpty(args.customerId)
      ? {customerId: args.customerId, recordId: args.recordId, resourceId: args.resourceId}
      : skipToken
  );
  const thread = useCommunicationThreadMessages(data?.messages ?? []);

  // A skipped query (no customer yet) is neither loading nor settled.
  return {...thread, isLoading, isError, isSettled: isSuccess || isError};
}

export function useCommunicationThreadMessages(messages: CustomerMessageResponseBody[]) {
  const [activeFilter, setActiveFilter] = useState<CommunicationChannelFilter>('all');
  const filterCounts = {
    all: messages.length,
    email: messages.filter((message) => message.channel === 'email').length,
    sms: messages.filter((message) => message.channel === 'sms').length,
    whatsapp: messages.filter((message) => message.channel === 'whatsapp').length,
    call: messages.filter((message) => message.channel === 'call').length,
  };
  const filteredMessages = messages.filter((message) =>
    match(activeFilter)
      .with('all', () => true)
      .with('email', () => message.channel === 'email')
      .with('sms', () => message.channel === 'sms')
      .with('whatsapp', () => message.channel === 'whatsapp')
      .with('call', () => message.channel === 'call')
      .exhaustive()
  );
  // Newest first: sort ascending, then reverse so the most recent message/day is on top.
  const groups = groupMessagesByDay(reverse(sortMessagesChronologically(filteredMessages)));

  return {activeFilter, setActiveFilter, filterCounts, groups, messages};
}
